import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "mindmap-container mt-5 pt-5" }
const _hoisted_2 = { "aria-label": "breadcrumb" }
const _hoisted_3 = { class: "breadcrumb mb-3" }
const _hoisted_4 = { class: "breadcrumb-item" }
const _hoisted_5 = { class: "toolbar" }
const _hoisted_6 = { class: "toolbar-left" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "toolbar-center" }
const _hoisted_9 = {
  key: 0,
  class: "flow-title-badge"
}
const _hoisted_10 = { class: "toolbar-right" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "stats-group" }
const _hoisted_13 = { class: "stat-chip stat-chip--blue" }
const _hoisted_14 = { class: "stat-chip stat-chip--green" }
const _hoisted_15 = { class: "stat-chip stat-chip--purple" }
const _hoisted_16 = {
  key: 0,
  class: "canvas-placeholder"
}
const _hoisted_17 = ["onMousedown", "onClick", "onDragover", "onDrop", "onMouseup"]
const _hoisted_18 = ["onMousedown"]
const _hoisted_19 = ["onMousedown"]
const _hoisted_20 = { class: "node-header" }
const _hoisted_21 = { class: "node-actions" }
const _hoisted_22 = ["onClick", "title"]
const _hoisted_23 = ["onClick"]
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 1,
  class: "node-footer"
}
const _hoisted_26 = {
  key: 0,
  class: "badge bg-secondary"
}
const _hoisted_27 = {
  key: 1,
  class: "badge bg-info ms-1"
}
const _hoisted_28 = { class: "connections-layer" }
const _hoisted_29 = ["d", "onClick"]
const _hoisted_30 = ["d", "stroke", "stroke-width", "onClick"]
const _hoisted_31 = { class: "canvas-controls" }
const _hoisted_32 = { class: "controls-group" }
const _hoisted_33 = { class: "controls-group" }
const _hoisted_34 = ["title"]
const _hoisted_35 = { class: "final-prompt-panel" }
const _hoisted_36 = { class: "final-prompt-header" }
const _hoisted_37 = { class: "prompt-header-actions" }
const _hoisted_38 = {
  key: 0,
  class: "prompt-char-count"
}
const _hoisted_39 = { class: "final-prompt-content" }
const _hoisted_40 = {
  key: 0,
  class: "final-prompt-text"
}
const _hoisted_41 = {
  key: 1,
  class: "prompt-empty-state"
}
const _hoisted_42 = { class: "modal-dialog modal-dialog-centered" }
const _hoisted_43 = { class: "modal-content" }
const _hoisted_44 = { class: "modal-body" }
const _hoisted_45 = { class: "mb-3" }
const _hoisted_46 = { class: "mb-3" }
const _hoisted_47 = { class: "mb-3" }
const _hoisted_48 = { class: "mb-3" }
const _hoisted_49 = ["value"]
const _hoisted_50 = { class: "mb-3" }
const _hoisted_51 = ["value"]
const _hoisted_52 = { class: "modal-footer" }
const _hoisted_53 = ["disabled"]

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import MindmapSidebar from "@/components/pms/admin/mindmap/MindmapSidebar.vue";
import { useMindmapStore, type Variable } from "@/store/mindmap";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";

const minZoom = 0.25;
const maxZoom = 2.5;
const zoomStep = 0.1;

// Pan state

export default /*@__PURE__*/_defineComponent({
  __name: 'Mindmap',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const mindmapStore = useMindmapStore();
const canvas = ref<HTMLCanvasElement | null>(null);
const canvasContainer = ref<HTMLDivElement | null>(null);
const canvasWrapper = ref<HTMLDivElement | null>(null);
const sidebarRef = ref<any>(null);

// Flow management state
const currentFlowId = ref<string | null>(null);
const flowTitle = ref<string>("");
const flowDescription = ref<string>("");
const isSavingFlow = ref(false);
const isLoadingFlow = ref(false);

// Add child node modal state
const showAddNodeModal = ref(false);
const parentNodeIdForNewChild = ref<string | null>(null);
const isCreatingNode = ref(false);
const categories = ref<any[]>([]);
const variableTypes = ref<any[]>([]);
const newNodeData = ref({
  title: "",
  slug: "",
  description: "",
  prompt_category_id: "",
  type_id: "",
  filterable: false,
  visible_on_card: false,
});

// Drag state
const draggedNodeId = ref<string | null>(null);
const dragOffset = ref({ x: 0, y: 0 });

// Zoom state
const zoom = ref(1);
const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);
const panStart = ref({ x: 0, y: 0 });

// Connection state (simplified)
const connectingFrom = ref<string | null>(null);
const selectedConnectionId = ref<string | null>(null);

// Full screen state
const isFullScreen = ref(false);

// ── OpenAI / Run Prompt state ─────────────────────────────────────────────────
const selectedModel = ref("gpt-5.1");
const isRunningPrompt = ref(false);
const aiResponse = ref("");
const responseTokenCount = ref(0);
const aiResponseContent = ref<HTMLDivElement | null>(null);
let abortController: AbortController | null = null;

// Watch fullscreen state to handle body scroll
watch(isFullScreen, (val) => {
  if (val) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

// Computed
const allNodes = computed(() => {
  const nodes = mindmapStore.allNodes;
  const visibleNodes: any[] = [];

  // Helper to check if node is a category
  const isCategoryNode = (node: any): boolean => {
    return node.description && node.description.includes("Category with");
  };

  // Helper to check if a node should be hidden
  const shouldHideNode = (node: any): boolean => {
    // Never hide if node itself is a category
    if (isCategoryNode(node)) {
      return false;
    }

    // Check if any parent category is collapsed
    let currentNode = node;
    while (currentNode.parentId) {
      const parent = nodes.find((n) => n.id === currentNode.parentId);
      if (!parent) break;

      // If parent is a collapsed category, hide this variable node
      if (isCategoryNode(parent) && parent.collapsed) {
        return true;
      }

      currentNode = parent;
    }
    return false;
  };

  // Filter nodes
  for (const node of nodes) {
    if (!shouldHideNode(node)) {
      visibleNodes.push(node);
    }
  }

  return visibleNodes;
});
const allConnections = computed(() => {
  // Filter connections to only show those between visible nodes
  const visibleNodeIds = new Set(allNodes.value.map((n: any) => n.id));
  return Array.from(mindmapStore.connections.values()).filter(
    (conn) =>
      visibleNodeIds.has(conn.sourceId) && visibleNodeIds.has(conn.targetId)
  );
});
const selectedNodeId = computed(() => mindmapStore.selectedNodeId);
const nodeCount = computed(() => mindmapStore.nodeCount);
const connectionCount = computed(() => mindmapStore.connectionCount);

// Generate final prompt from all nodes hierarchically
const finalPrompt = computed(() => {
  const nodes = allNodes.value;
  if (nodes.length === 0) return "";

  const lines: string[] = [];
  const processedNodes = new Set<string>();

  // Helper function to check if node is a category
  const isCategoryNode = (node: any) => {
    return node.description && node.description.includes("Category with");
  };

  // Helper function to check if node is start node
  const isStartNode = (node: any) => {
    return (
      node.id === "start_node" ||
      (node.variableId === "start" && node.isDeletable === false)
    );
  };

  // Helper function to process a node and its children recursively
  const processNode = (nodeId: string, depth: number = 0) => {
    if (processedNodes.has(nodeId)) return;
    processedNodes.add(nodeId);

    const node = mindmapStore.getNodeById(nodeId);
    if (!node) return;

    // Only add start node description, skip category nodes
    if (isStartNode(node)) {
      if (node.description) {
        lines.push(node.description);
      }
    } else if (!isCategoryNode(node)) {
      // For variable nodes, add title and description
      if (node.title) {
        lines.push(node.title);
      }
      if (node.description) {
        lines.push(node.description);
      }
    }

    // Process children in order (including children of category nodes)
    if (node.children && node.children.length > 0) {
      node.children.forEach((childId) => {
        processNode(childId, depth + 1);
      });
    }
  };

  // Start with root nodes (nodes without parents)
  const rootNodes = nodes.filter((n) => !n.parentId || n.parentId === null);
  rootNodes.forEach((rootNode) => {
    processNode(rootNode.id);
  });

  return lines.join("\n");
});

// Get the full selected node object for the sidebar
const selectedNode = computed(() => {
  if (!selectedNodeId.value) return null;
  return mindmapStore.nodes.get(selectedNodeId.value) || null;
});

// Drag and Drop handlers
const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "copy";
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();

  try {
    const jsonData = event.dataTransfer?.getData("application/json");
    const itemType = event.dataTransfer?.getData("item-type");

    if (!jsonData) {
      console.warn("No JSON data in drop event");
      return;
    }

    if (!canvasContainer.value) return;

    // Calculate position accounting for zoom and pan
    const canvasX = (event.clientX - panX.value) / zoom.value;
    const canvasY = (event.clientY - panY.value) / zoom.value;

    const x = canvasX - 100; // Center the node
    const y = canvasY - 50;

    // Check if it's a category or variable
    if (itemType === "category") {
      handleCategoryDrop(jsonData, x, y);
    } else {
      // Handle as variable (default)
      const variable: Variable = JSON.parse(jsonData);
      const newNode = mindmapStore.createRootNode(variable, { x, y });

      toast.success(`Node "${newNode.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });

      console.log("Dropped variable on canvas:", variable, "at position:", {
        x,
        y,
      });
    }
  } catch (error) {
    console.error("Error handling drop:", error);
    toast.error("Failed to create node", {
      position: "top-right",
      autoClose: 3000,
    });
  }
};

// Category drop handler
const handleCategoryDrop = (jsonData: string, x: number, y: number) => {
  try {
    const data = JSON.parse(jsonData);
    const { category, variables } = data;

    if (!category) {
      console.error("Invalid category data");
      return;
    }

    // Create parent node for the category
    const categoryVariable: Variable = {
      _id: category._id,
      prompt_category_id: category._id,
      title: category.title,
      description: `Category with ${variables?.length || 0} variables`,
      type_id: "",
      icon: null,
      slug: category.title.toLowerCase().replace(/\s+/g, "-"),
      filterable: false,
      visible_on_card: false,
      created_at: new Date().toISOString(),
      updated_at: null,
      deleted_at: null,
      updated_by: null,
      deleted_by: null,
      is_trash: false,
      __v: 0,
    };

    const parentNode = mindmapStore.createRootNode(categoryVariable, { x, y });

    // Create child nodes for each variable
    if (variables && Array.isArray(variables) && variables.length > 0) {
      variables.forEach((variable: Variable, index: number) => {
        const childX = x + 250; // Position to the right of parent
        const childY = y + index * 120; // Stack vertically

        mindmapStore.createChildNode(variable, parentNode.id, {
          x: childX,
          y: childY,
        });
      });

      toast.success(
        `Category "${category.title}" created with ${variables.length} variables!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } else {
      toast.success(`Category "${category.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });
    }

    console.log(
      "Dropped category:",
      category.title,
      "with",
      variables?.length || 0,
      "variables"
    );
  } catch (error) {
    console.error("Error creating category nodes:", error);
    toast.error("Failed to create category", {
      position: "top-right",
      autoClose: 3000,
    });
  }
};

// Node drag handlers
const handleNodeMouseDown = (event: MouseEvent, nodeId: string) => {
  // Don't start node dragging if we're connecting from an anchor
  if (connectingFrom.value) return;

  const node = mindmapStore.getNodeById(nodeId);
  if (!node) return;

  draggedNodeId.value = nodeId;

  // Calculate offset accounting for current zoom and pan
  const canvasX = (event.clientX - panX.value) / zoom.value;
  const canvasY = (event.clientY - panY.value) / zoom.value;

  dragOffset.value = {
    x: canvasX - node.x,
    y: canvasY - node.y,
  };

  event.preventDefault();
  event.stopPropagation();
};

const handleMouseMove = (event: MouseEvent) => {
  if (!draggedNodeId.value) return;

  // Calculate position accounting for zoom and pan
  const canvasX = (event.clientX - panX.value) / zoom.value;
  const canvasY = (event.clientY - panY.value) / zoom.value;

  const x = canvasX - dragOffset.value.x;
  const y = canvasY - dragOffset.value.y;

  mindmapStore.updateNodePosition(draggedNodeId.value, { x, y });
};

const handleMouseUp = () => {
  draggedNodeId.value = null;
};

// Node click handler
const handleNodeClick = (nodeId: string) => {
  mindmapStore.selectNode(nodeId);
};

// Canvas click handler
const handleCanvasClick = () => {
  mindmapStore.selectNode(null);
};

// Deselect node (for properties panel close button)
const handleDeselectNode = () => {
  mindmapStore.selectNode(null);
};

// Node drag over for creating child nodes
const handleNodeDragOver = (event: DragEvent, nodeId: string) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "link";
  }
};

// Node drop handler for creating child nodes
const handleNodeDrop = (event: DragEvent, parentId: string) => {
  event.preventDefault();

  try {
    const jsonData = event.dataTransfer?.getData("application/json");
    if (!jsonData) return;

    const variable: Variable = JSON.parse(jsonData);

    const parent = mindmapStore.getNodeById(parentId);
    if (!parent) return;

    // Position child node to the right of parent
    const x = parent.x + parent.width + 150;
    const y = parent.y + parent.children.length * 100;

    const newNode = mindmapStore.createChildNode(variable, parentId, { x, y });

    if (newNode) {
      toast.success(`Child node "${newNode.title}" created!`, {
        position: "top-right",
        autoClose: 2000,
      });
    }
  } catch (error) {
    console.error("Error creating child node:", error);
  }
};

// Delete node handler
const handleDeleteNode = (nodeId: string) => {
  const node = mindmapStore.getNodeById(nodeId);
  if (!node) return;

  // Prevent deletion of non-deletable nodes (like Start node)
  if (node.isDeletable === false) {
    toast.warning("This node cannot be deleted", {
      position: "top-right",
      autoClose: 2000,
    });
    return;
  }

  if (
    confirm(
      `Are you sure you want to delete "${node.title}" and all its children?`
    )
  ) {
    mindmapStore.deleteNode(nodeId);
    toast.info("Node deleted", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Helper to check if node is a category
const isCategory = (node: any) => {
  return node.description && node.description.includes("Category with");
};

// Toggle node collapse/expand
const handleToggleCollapse = (nodeId: string) => {
  mindmapStore.toggleNodeCollapse(nodeId);
};

// Get count of variable children that are hidden when a category is collapsed
const getCollapsedVariableCount = (node: any): number => {
  if (!node.children || node.children.length === 0) {
    return 0;
  }

  const allNodes = mindmapStore.allNodes;
  let count = 0;

  for (const childId of node.children) {
    const childNode = allNodes.find((n) => n.id === childId);
    if (childNode) {
      // Only count if child is NOT a category
      const isChildCategory =
        childNode.description &&
        childNode.description.includes("Category with");
      if (!isChildCategory) {
        count++;
      }
    }
  }

  return count;
};

// Copy final prompt to clipboard
const copyFinalPrompt = async () => {
  if (!finalPrompt.value) {
    toast.warning("No prompt to copy", {
      position: "top-right",
      autoClose: 2000,
    });
    return;
  }

  try {
    await navigator.clipboard.writeText(finalPrompt.value);
    toast.success("Prompt copied to clipboard!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch (err) {
    console.error("Failed to copy:", err);
    toast.error("Failed to copy prompt", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// ── Format AI markdown-like response to HTML ──────────────────────────────────
const formattedAiResponse = computed(() => {
  if (!aiResponse.value) return "";
  let text = aiResponse.value;

  // Code blocks (``` ... ```)
  text = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="ai-code-block"><code class="lang-${lang || 'text'}">${escapeHtml(code.trim())}</code></pre>`;
  });

  // Inline code
  text = text.replace(/`([^`]+)`/g, '<code class="ai-inline-code">$1</code>');

  // Bold (**text**)
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic (*text*)
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Headers (### ## #)
  text = text.replace(/^### (.+)$/gm, '<h5 class="ai-h5">$1</h5>');
  text = text.replace(/^## (.+)$/gm, '<h4 class="ai-h4">$1</h4>');
  text = text.replace(/^# (.+)$/gm, '<h3 class="ai-h3">$1</h3>');

  // Unordered lists
  text = text.replace(/^[-*] (.+)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Numbered lists
  text = text.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Line breaks
  text = text.replace(/\n\n/g, '</p><p>');
  text = text.replace(/\n/g, '<br/>');
  text = `<p>${text}</p>`;

  return text;
});

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Handle Run Prompt button click from toolbar
const handleRunPromptClick = () => {
  if (sidebarRef.value) {
    sidebarRef.value.setActiveTab("chat");
  }
  runFinalPrompt();
};


// ── Run prompt via OpenAI streaming API ───────────────────────────────────────
const runFinalPrompt = async () => {
  if (!finalPrompt.value) {
    toast.warning("No prompt to run. Add nodes to the canvas first.", {
      position: "top-right",
      autoClose: 3000,
    });
    return;
  }

  // Reset previous response
  aiResponse.value = "";
  responseTokenCount.value = 0;
  isRunningPrompt.value = true;

  // Scroll to response panel
  await nextTick();
  aiResponseContent.value?.scrollIntoView({ behavior: "smooth", block: "start" });

  abortController = new AbortController();

  try {
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.VUE_APP_OPENAI_API_KEY}`,
      },
      signal: abortController.signal,
      body: JSON.stringify({
        model: selectedModel.value,
        stream: true,
        messages: [
          {
            role: "system",
            content:
              "You are a helpful AI assistant. Format your responses clearly using markdown where appropriate.",
          },
          {
            role: "user",
            content: finalPrompt.value,
          },
        ],
      }),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(
        errData?.error?.message || `API error: ${response.status}`
      );
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder("utf-8");

    if (!reader) throw new Error("No response body");

    let charCount = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n").filter((l) => l.trim());

      for (const line of lines) {
        if (line === "data: [DONE]") break;
        if (!line.startsWith("data: ")) continue;

        try {
          const json = JSON.parse(line.slice(6));
          const delta = json?.choices?.[0]?.delta?.content;
          if (delta) {
            aiResponse.value += delta;
            charCount += delta.length;
            responseTokenCount.value = Math.ceil(charCount / 4); // rough estimate

            // Auto-scroll response panel
            await nextTick();
            if (aiResponseContent.value) {
              aiResponseContent.value.scrollTop =
                aiResponseContent.value.scrollHeight;
            }
          }
        } catch {
          // skip malformed chunks
        }
      }
    }

    toast.success("AI response generated!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      toast.info("Generation stopped.", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      console.error("OpenAI error:", err);
      toast.error(err?.message || "Failed to get AI response", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  } finally {
    isRunningPrompt.value = false;
    abortController = null;
  }
};

// Stop streaming
const stopPromptRun = () => {
  abortController?.abort();
};

// Copy AI response
const copyAiResponse = async () => {
  if (!aiResponse.value) return;
  try {
    await navigator.clipboard.writeText(aiResponse.value);
    toast.success("AI response copied!", {
      position: "top-right",
      autoClose: 2000,
    });
  } catch {
    toast.error("Failed to copy response", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Clear AI response
const clearAiResponse = () => {
  aiResponse.value = "";
  responseTokenCount.value = 0;
};

// Show add child modal
const showAddChildModal = (nodeId: string) => {
  const parentNode = mindmapStore.getNodeById(nodeId);
  if (!parentNode) return;

  // Find the category ID from the parent node
  // If parent is a category, use its variableId as the category ID
  const categoryId = parentNode.variableId || "";

  parentNodeIdForNewChild.value = nodeId;
  newNodeData.value.prompt_category_id = categoryId;
  showAddNodeModal.value = true;
};

// Handle add child from panel
const handleAddChildNodeFromPanel = (nodeId: string) => {
  showAddChildModal(nodeId);
};

// Close add node modal
const closeAddNodeModal = () => {
  showAddNodeModal.value = false;
  parentNodeIdForNewChild.value = null;
  newNodeData.value = {
    title: "",
    slug: "",
    description: "",
    prompt_category_id: "",
    type_id: "",
    filterable: false,
    visible_on_card: false,
  };
};

// Submit new node
const submitNewNode = async () => {
  if (!parentNodeIdForNewChild.value) return;

  isCreatingNode.value = true;
  try {
    // Create variable via API
    const response = await apiService.post(
      "/common/prompt-variables",
      newNodeData.value
    );

    // Get parent node to calculate child position
    const parentNode = mindmapStore.getNodeById(parentNodeIdForNewChild.value);
    if (!parentNode) {
      throw new Error("Parent node not found");
    }

    // Calculate position for new child (below and to the right of parent)
    const childPosition = {
      x: parentNode.x + parentNode.width + 50,
      y: parentNode.y + parentNode.children.length * 120,
    };

    // Create the child node in the mindmap
    const variable: Variable = {
      _id: response._id,
      prompt_category_id: response.prompt_category_id,
      title: response.title,
      description: response.description || "",
      type_id: response.type_id || "",
      icon: response.icon || null,
      slug: response.slug,
      filterable: response.filterable || false,
      visible_on_card: response.visible_on_card || false,
      created_at: response.created_at,
      updated_at: response.updated_at,
      deleted_at: null,
      updated_by: null,
      deleted_by: null,
      is_trash: false,
      __v: 0,
    };

    mindmapStore.createChildNode(
      variable,
      parentNodeIdForNewChild.value,
      childPosition
    );

    toast.success("Child node created successfully", {
      position: "top-right",
      autoClose: 2000,
    });

    closeAddNodeModal();
  } catch (error: any) {
    console.error("Error creating node:", error);
    toast.error(error?.response?.data?.message || "Failed to create node", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isCreatingNode.value = false;
  }
};

// Load categories and types
const loadCategoriesAndTypes = async () => {
  try {
    const [categoriesResponse, typesResponse] = await Promise.all([
      apiService.get("/common/prompt-categories"),
      apiService.get("/common/variable-types"),
    ]);
    categories.value = categoriesResponse || [];
    variableTypes.value = typesResponse || [];
  } catch (error) {
    console.error("Error loading categories/types:", error);
  }
};

// Clear canvas handler
const handleClearCanvas = () => {
  if (nodeCount.value === 0) return;

  if (confirm("Are you sure you want to clear the entire canvas?")) {
    mindmapStore.clearCanvas();
    toast.info("Canvas cleared", {
      position: "top-right",
      autoClose: 2000,
    });
  }
};

// Zoom handlers
const handleZoomIn = () => {
  zoom.value = Math.min(maxZoom, zoom.value + zoomStep);
};

const handleZoomOut = () => {
  zoom.value = Math.max(minZoom, zoom.value - zoomStep);
};

const handleResetZoom = () => {
  zoom.value = 1;
};

const handleWheel = (event: WheelEvent) => {
  // Prevent the browser from scrolling the page
  event.preventDefault();
  event.stopPropagation();

  if (event.ctrlKey || event.metaKey) {
    // Ctrl/Cmd + scroll (or pinch gesture which browsers map to ctrlKey+wheel) → ZOOM
    const delta = event.deltaY > 0 ? -zoomStep : zoomStep;
    zoom.value = Math.max(minZoom, Math.min(maxZoom, zoom.value + delta));
  } else {
    // Regular two-finger scroll → PAN
    panX.value -= event.deltaX;
    panY.value -= event.deltaY;
  }
};

const handleFitToScreen = () => {
  // Toggle fullscreen state
  isFullScreen.value = !isFullScreen.value;

  if (nodeCount.value === 0) {
    zoom.value = 1;
    return;
  }

  // Calculate bounding box of all nodes
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  allNodes.value.forEach((node) => {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  });

  if (!canvasContainer.value) return;

  // Wait for next tick to ensure container Rect is updated if fullscreen changed
  setTimeout(() => {
    if (!canvasContainer.value) return;
    const containerRect = canvasContainer.value.getBoundingClientRect();
    const contentWidth = maxX - minX + 100; // Add padding
    const contentHeight = maxY - minY + 100;

    const scaleX = containerRect.width / contentWidth;
    const scaleY = containerRect.height / contentHeight;

    zoom.value = 1;
  }, 100);
};

// Pan handlers
const handlePanStart = (event: MouseEvent) => {
  // Don't pan if dragging a node or connecting
  if (draggedNodeId.value) return;
  if (connectingFrom.value) return;

  // Pan with left-click drag on empty canvas, OR middle mouse button
  if (event.button === 0 || event.button === 1) {
    isPanning.value = true;
    panStart.value = {
      x: event.clientX - panX.value,
      y: event.clientY - panY.value,
    };
    event.preventDefault();
  }
};

const handlePanMove = (event: MouseEvent) => {
  if (!isPanning.value) return;

  panX.value = event.clientX - panStart.value.x;
  panY.value = event.clientY - panStart.value.y;
};

const handlePanEnd = () => {
  isPanning.value = false;
};

const handleResetView = () => {
  zoom.value = 1;
  panX.value = 0;
  panY.value = 0;
  toast.info("View reset", {
    position: "top-right",
    autoClose: 1500,
  });
};

// Connection mode handlers
const handleAnchorClick = (nodeId: string) => {
  if (!connectingFrom.value) {
    // First click - start connection
    connectingFrom.value = nodeId;
    toast.info("Click another node to connect", {
      position: "top-right",
      autoClose: 2000,
    });
  } else if (connectingFrom.value === nodeId) {
    // Clicked same node - cancel
    connectingFrom.value = null;
    toast.info("Connection cancelled", {
      position: "top-right",
      autoClose: 1500,
    });
  } else {
    // Second click - create connection
    const sourceId = connectingFrom.value;
    const targetId = nodeId;

    // Check if connection already exists
    const existingConnection = Array.from(
      mindmapStore.connections.values()
    ).find((conn) => conn.sourceId === sourceId && conn.targetId === targetId);

    if (existingConnection) {
      toast.warning("Connection already exists", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Create the connection
      const result = mindmapStore.createManualConnection(sourceId, targetId);
      if (result) {
        toast.success("Connection created!", {
          position: "top-right",
          autoClose: 1500,
        });
      } else {
        toast.error("Failed to create connection", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    }

    connectingFrom.value = null;
  }
};

// Connection handlers (drag-from-dot-to-drop-on-node)
const handleAnchorMouseDown = (event: MouseEvent, nodeId: string) => {
  event.preventDefault();
  event.stopPropagation();

  connectingFrom.value = nodeId;

  toast.info("Release on another node to connect", {
    position: "top-right",
    autoClose: 1500,
  });
};

const handleNodeMouseUp = (event: MouseEvent, nodeId: string) => {
  if (!connectingFrom.value) return;

  event.preventDefault();
  event.stopPropagation();

  if (connectingFrom.value === nodeId) {
    // Released on same node - cancel
    connectingFrom.value = null;
    toast.info("Connection cancelled", {
      position: "top-right",
      autoClose: 1500,
    });
    return;
  }

  // Create connection
  const sourceId = connectingFrom.value;
  const targetId = nodeId;

  // Check if connection already exists
  const existingConnection = Array.from(mindmapStore.connections.values()).find(
    (conn) => conn.sourceId === sourceId && conn.targetId === targetId
  );

  if (existingConnection) {
    toast.warning("Connection already exists", {
      position: "top-right",
      autoClose: 2000,
    });
  } else {
    const result = mindmapStore.createManualConnection(sourceId, targetId);
    if (result) {
      toast.success("Connection created!", {
        position: "top-right",
        autoClose: 1500,
      });
    } else {
      toast.error("Failed to create connection", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  }

  connectingFrom.value = null;
};

const handleConnectionClick = (connectionId: string) => {
  selectedConnectionId.value = connectionId;
  if (confirm("Delete this connection?")) {
    mindmapStore.deleteConnection(connectionId);
    selectedConnectionId.value = null;
    toast.info("Connection deleted", {
      position: "top-right",
      autoClose: 1500,
    });
  } else {
    selectedConnectionId.value = null;
  }
};

// Keyboard shortcuts
const handleKeyDown = (event: KeyboardEvent) => {
  // Don't handle keyboard shortcuts if user is typing in an input/textarea
  const target = event.target as HTMLElement;
  if (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  ) {
    return;
  }

  const selectedNode = mindmapStore.selectedNode;
  const nodes = allNodes.value;

  // Tab key to cycle through category nodes
  if (event.key === "Tab") {
    event.preventDefault();
    const categoryNodes = nodes.filter((node) => isCategory(node));

    if (categoryNodes.length === 0) return;

    if (!selectedNode) {
      // Select first category
      mindmapStore.selectNode(categoryNodes[0].id);
    } else {
      const currentIndex = categoryNodes.findIndex(
        (n) => n.id === selectedNode.id
      );

      if (currentIndex === -1) {
        // Current selection is not a category, select first category
        mindmapStore.selectNode(categoryNodes[0].id);
      } else {
        // Cycle to next category (or loop back to first)
        const nextIndex = event.shiftKey
          ? (currentIndex - 1 + categoryNodes.length) % categoryNodes.length // Shift+Tab goes backward
          : (currentIndex + 1) % categoryNodes.length; // Tab goes forward
        mindmapStore.selectNode(categoryNodes[nextIndex].id);
      }
    }
    return;
  }

  // Arrow keys to navigate between nodes
  if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
    event.preventDefault();

    if (!selectedNode) {
      // No node selected, select the first one
      if (nodes.length > 0) {
        mindmapStore.selectNode(nodes[0].id);
      }
      return;
    }

    const currentNode = mindmapStore.getNodeById(selectedNode.id);
    if (!currentNode) return;

    // Find the closest node in the direction of the arrow key
    let closestNode = null;
    let closestDistance = Infinity;

    nodes.forEach((node) => {
      if (node.id === selectedNode.id) return;

      const dx = node.x - currentNode.x;
      const dy = node.y - currentNode.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Check if node is in the right direction
      let isInDirection = false;
      const threshold = 50; // Tolerance for directional navigation

      switch (event.key) {
        case "ArrowUp":
          isInDirection = dy < -threshold && Math.abs(dx) < Math.abs(dy);
          break;
        case "ArrowDown":
          isInDirection = dy > threshold && Math.abs(dx) < Math.abs(dy);
          break;
        case "ArrowLeft":
          isInDirection = dx < -threshold && Math.abs(dy) < Math.abs(dx);
          break;
        case "ArrowRight":
          isInDirection = dx > threshold && Math.abs(dy) < Math.abs(dx);
          break;
      }

      if (isInDirection && distance < closestDistance) {
        closestDistance = distance;
        closestNode = node;
      }
    });

    if (closestNode) {
      mindmapStore.selectNode(closestNode.id);
    }
    return;
  }

  // Delete/Backspace to delete selected node
  if ((event.key === "Delete" || event.key === "Backspace") && selectedNode) {
    event.preventDefault();
    handleDeleteNode(selectedNode.id);
    return;
  }

  // Escape to cancel connection or deselect node
  if (event.key === "Escape") {
    if (connectingFrom.value) {
      connectingFrom.value = null;
      toast.info("Connection cancelled", {
        position: "top-right",
        autoClose: 1500,
      });
    } else if (selectedNode) {
      mindmapStore.selectNode(null);
    }
    return;
  }

  // Delete selected connection with Delete key (only if no node is selected)
  if (event.key === "Delete" && !selectedNode && selectedConnectionId.value) {
    mindmapStore.deleteConnection(selectedConnectionId.value);
    selectedConnectionId.value = null;
    toast.info("Connection deleted", {
      position: "top-right",
      autoClose: 1500,
    });
  }

  // Ctrl+Enter to add child node (only for category nodes)
  if (event.ctrlKey && event.key === "Enter" && selectedNode) {
    const node = mindmapStore.getNodeById(selectedNode.id);
    if (node && isCategory(node)) {
      event.preventDefault();
      showAddChildModal(selectedNode.id);
    }
  }
};

// Save flow to database
const handleSaveFlow = async () => {
  if (!flowTitle.value) {
    flowTitle.value = prompt("Enter a title for this flow:") || "Untitled Flow";
    if (!flowTitle.value || flowTitle.value === "Untitled Flow") {
      toast.warning("Please provide a flow title", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
  }

  isSavingFlow.value = true;
  try {
    const flowData = {
      title: flowTitle.value,
      description: flowDescription.value,
      nodes: mindmapStore.allNodes,
      connections: Array.from(mindmapStore.connections.values()),
      canvas_settings: {
        offset_x: panX.value,
        offset_y: panY.value,
        scale: zoom.value,
      },
      metadata: {
        node_count: mindmapStore.nodeCount,
        connection_count: mindmapStore.connectionCount,
      },
    };

    console.log("Saving flow with nodes:", flowData.nodes.length);
    console.log(
      "Start node exists?",
      flowData.nodes.some((n) => n.id === "start_node")
    );

    let response;
    if (currentFlowId.value) {
      // Update existing flow
      response = await apiService.put(
        "/common/prompt-flows",
        currentFlowId.value,
        flowData
      );
      toast.success("Flow updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Create new flow
      response = await apiService.post("/common/prompt-flows", flowData);
      if (response.data && response.data._id) {
        currentFlowId.value = response.data._id;
        // Update URL with flowId
        router.replace({
          name: "Mindmap",
          query: { flowId: currentFlowId.value },
        });
      }
      toast.success("Flow saved successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  } catch (error) {
    console.error("Error saving flow:", error);
    toast.error("Failed to save flow", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isSavingFlow.value = false;
  }
};

// Load flow from database
const loadFlow = async (flowId: string) => {
  isLoadingFlow.value = true;
  try {
    const response = await apiService.get(`/common/prompt-flows/${flowId}`);
    console.log("API Response:", response);
    const flowData = response;
    console.log("Flow Data:", flowData);

    if (flowData) {
      // Set flow metadata
      currentFlowId.value = flowData._id;
      flowTitle.value = flowData.title || "";
      flowDescription.value = flowData.description || "";

      console.log("Flow nodes from API:", flowData.nodes);
      console.log("Number of nodes from API:", flowData.nodes?.length || 0);

      // Import mindmap data
      const importData = {
        nodes: flowData.nodes || [],
        connections: flowData.connections || [],
        metadata: flowData.metadata || {},
      };

      console.log("Import data:", importData);
      const success = mindmapStore.importMindmap(importData);

      console.log(
        "After import - Total nodes in store:",
        mindmapStore.allNodes.length
      );
      console.log(
        "Nodes after import:",
        mindmapStore.allNodes.map((n) => ({ id: n.id, title: n.title }))
      );

      // Restore canvas settings
      if (flowData.canvas_settings) {
        panX.value = flowData.canvas_settings.offset_x || 0;
        panY.value = flowData.canvas_settings.offset_y || 0;
        zoom.value = flowData.canvas_settings.scale || 1;
      }

      if (success) {
        toast.success("Flow loaded successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    }
  } catch (error) {
    console.error("Error loading flow:", error);
    toast.error("Failed to load flow", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    isLoadingFlow.value = false;
  }
};

// Navigate back to flows list
const handleBackToList = () => {
  router.push({ name: "prompt-flows" });
};

// Export handler
const handleExport = () => {
  const data = mindmapStore.exportMindmap();
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `mindmap-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);

  toast.success("Mindmap exported!", {
    position: "top-right",
    autoClose: 2000,
  });
};

// Import handler
const handleImport = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "application/json";
  input.onchange = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        const success = mindmapStore.importMindmap(data);

        if (success) {
          toast.success("Mindmap imported successfully!", {
            position: "top-right",
            autoClose: 2000,
          });
        } else {
          toast.error("Failed to import mindmap", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Error importing mindmap:", error);
        toast.error("Invalid mindmap file", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };
    reader.readAsText(file);
  };
  input.click();
};

// Connection path calculation
const getConnectionPath = (connection: any) => {
  const source = mindmapStore.getNodeById(connection.sourceId);
  const target = mindmapStore.getNodeById(connection.targetId);

  if (!source || !target) return "";

  const x1 = source.x + source.width;
  const y1 = source.y + source.height / 2;
  const x2 = target.x;
  const y2 = target.y + target.height / 2;

  const midX = (x1 + x2) / 2;

  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
};

onMounted(async () => {
  console.log("Mindmap component mounted");

  // Load categories and types for add node modal
  loadCategoriesAndTypes();

  // Check if there's a flowId in the URL query
  const flowId = route.query.flowId as string;
  if (flowId) {
    // Load existing flow from database (importMindmap ensures start node exists)
    await loadFlow(flowId);
  } else {
    // For new flow, ensure start node exists
    if (!mindmapStore.nodes.get("start_node")) {
      console.log("Creating start node for new flow");
      mindmapStore.createStartNode();
    }
  }

  // Add global mouse event listeners for node dragging and panning
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("mousemove", handlePanMove);
  document.addEventListener("mouseup", handlePanEnd);

  // Add keyboard shortcuts
  document.addEventListener("keydown", handleKeyDown);

  // Attach wheel listener with { passive: false } so we can call preventDefault()
  // We must use this instead of @wheel.prevent on the template for proper touch support
  const wrapper = canvasWrapper.value;
  if (wrapper) {
    wrapper.addEventListener("wheel", handleWheel, { passive: false });
  }
});

onUnmounted(() => {
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("mousemove", handlePanMove);
  document.removeEventListener("mouseup", handlePanEnd);
  document.removeEventListener("keydown", handleKeyDown);

  const wrapper = canvasWrapper.value;
  if (wrapper) {
    wrapper.removeEventListener("wheel", handleWheel);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("nav", _hoisted_2, [
      _createElementVNode("ol", _hoisted_3, [
        _createElementVNode("li", _hoisted_4, [
          _createVNode(_component_router_link, { to: "/" }, {
            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
              _createTextVNode("Home", -1)
            ]))]),
            _: 1
          })
        ]),
        _cache[6] || (_cache[6] = _createElementVNode("li", {
          class: "breadcrumb-item active",
          "aria-current": "page"
        }, "Mindmap", -1))
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--primary",
          onClick: handleSaveFlow,
          title: "Save Flow (Ctrl+S)",
          disabled: isSavingFlow.value
        }, [
          _createElementVNode("i", {
            class: _normalizeClass(isSavingFlow.value ? 'fa fa-spinner fa-spin' : 'fa fa-save')
          }, null, 2),
          _createElementVNode("span", null, _toDisplayString(isSavingFlow.value ? "Saving..." : "Save Flow"), 1)
        ], 8, _hoisted_7),
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--ghost",
          onClick: handleBackToList,
          title: "Back to Flows List"
        }, [...(_cache[7] || (_cache[7] = [
          _createElementVNode("i", { class: "fa fa-arrow-left" }, null, -1),
          _createElementVNode("span", null, "Flows List", -1)
        ]))]),
        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "toolbar-divider" }, null, -1)),
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--ghost toolbar-btn--icon",
          onClick: handleExport,
          title: "Export Mindmap"
        }, [...(_cache[8] || (_cache[8] = [
          _createElementVNode("i", { class: "fa fa-download" }, null, -1)
        ]))]),
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--ghost toolbar-btn--icon",
          onClick: handleImport,
          title: "Import Mindmap"
        }, [...(_cache[9] || (_cache[9] = [
          _createElementVNode("i", { class: "fa fa-upload" }, null, -1)
        ]))]),
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--danger-ghost toolbar-btn--icon",
          onClick: handleClearCanvas,
          title: "Clear Canvas"
        }, [...(_cache[10] || (_cache[10] = [
          _createElementVNode("i", { class: "fa fa-trash" }, null, -1)
        ]))])
      ]),
      _createElementVNode("div", _hoisted_8, [
        (currentFlowId.value || flowTitle.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
              _cache[12] || (_cache[12] = _createElementVNode("i", { class: "fa fa-sitemap me-1" }, null, -1)),
              _createTextVNode(" " + _toDisplayString(flowTitle.value || "Untitled Flow"), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--run me-2",
          onClick: handleRunPromptClick,
          disabled: !finalPrompt.value || isRunningPrompt.value,
          title: "Run prompt with AI"
        }, [
          _createElementVNode("i", {
            class: _normalizeClass(isRunningPrompt.value ? 'fa fa-spinner fa-spin' : 'fa fa-play')
          }, null, 2),
          _createElementVNode("span", null, _toDisplayString(isRunningPrompt.value ? "Running..." : "Run Prompt"), 1)
        ], 8, _hoisted_11),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _cache[13] || (_cache[13] = _createElementVNode("i", { class: "fa fa-circle-nodes" }, null, -1)),
            _createElementVNode("span", null, _toDisplayString(nodeCount.value), 1),
            _cache[14] || (_cache[14] = _createElementVNode("small", null, "Nodes", -1))
          ]),
          _createElementVNode("div", _hoisted_14, [
            _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fa fa-link" }, null, -1)),
            _createElementVNode("span", null, _toDisplayString(connectionCount.value), 1),
            _cache[16] || (_cache[16] = _createElementVNode("small", null, "Links", -1))
          ]),
          _createElementVNode("div", _hoisted_15, [
            _cache[17] || (_cache[17] = _createElementVNode("i", { class: "fa fa-search-plus" }, null, -1)),
            _createElementVNode("span", null, _toDisplayString(Math.round(zoom.value * 100)) + "%", 1),
            _cache[18] || (_cache[18] = _createElementVNode("small", null, "Zoom", -1))
          ])
        ])
      ])
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["mindmap-wrapper", { 'is-fullscreen': isFullScreen.value }])
    }, [
      _createElementVNode("div", {
        class: "canvas-wrapper",
        ref_key: "canvasWrapper",
        ref: canvasWrapper
      }, [
        _createElementVNode("div", {
          ref_key: "canvasContainer",
          ref: canvasContainer,
          class: "canvas-area",
          style: _normalizeStyle({
            transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
            transformOrigin: 'top left',
            cursor: isPanning.value ? 'grabbing' : (connectingFrom.value ? 'crosshair' : 'default'),
          }),
          onDragover: handleDragOver,
          onDrop: handleDrop,
          onClick: handleCanvasClick,
          onMousedown: handlePanStart
        }, [
          _createElementVNode("canvas", {
            ref_key: "canvas",
            ref: canvas,
            id: "mindmap-canvas"
          }, null, 512),
          (nodeCount.value === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [...(_cache[19] || (_cache[19] = [
                _createStaticVNode("<div class=\"placeholder-icon\" data-v-04168770><i class=\"fa fa-diagram-project\" data-v-04168770></i></div><h5 data-v-04168770>Start Building Your Mindmap</h5><p data-v-04168770>Drag variables or categories from the right panel onto this canvas</p><div class=\"placeholder-hints\" data-v-04168770><div class=\"hint-item\" data-v-04168770><kbd data-v-04168770>Two-finger drag</kbd> Pan canvas</div><div class=\"hint-item\" data-v-04168770><kbd data-v-04168770>Ctrl</kbd>+<kbd data-v-04168770>Scroll</kbd> Zoom</div><div class=\"hint-item\" data-v-04168770><kbd data-v-04168770>Left-drag</kbd> Move nodes</div></div>", 4)
              ]))]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allNodes.value, (node) => {
            return (_openBlock(), _createElementBlock("div", {
              key: node.id,
              class: _normalizeClass(["mindmap-node", {
              selected: selectedNodeId.value === node.id,
              root: node.isRoot,
              'connect-source': connectingFrom.value === node.id,
              'connect-target': connectingFrom.value && connectingFrom.value !== node.id,
            }]),
              style: _normalizeStyle({
              left: `${node.x}px`,
              top: `${node.y}px`,
              width: `${node.width}px`,
              minHeight: `${node.height}px`,
              backgroundColor: node.color,
            }),
              onMousedown: ($event: any) => (handleNodeMouseDown($event, node.id)),
              onClick: _withModifiers(($event: any) => (handleNodeClick(node.id)), ["stop"]),
              onDragover: _withModifiers(($event: any) => (handleNodeDragOver($event, node.id)), ["prevent"]),
              onDrop: _withModifiers(($event: any) => (handleNodeDrop($event, node.id)), ["stop"]),
              onMouseup: ($event: any) => (handleNodeMouseUp($event, node.id))
            }, [
              _createElementVNode("div", {
                class: "connection-anchor anchor-left",
                onMousedown: _withModifiers(($event: any) => (handleAnchorMouseDown($event, node.id)), ["stop"]),
                title: "Drag to connect nodes"
              }, [...(_cache[20] || (_cache[20] = [
                _createElementVNode("div", { class: "anchor-dot" }, null, -1)
              ]))], 40, _hoisted_18),
              _createElementVNode("div", {
                class: "connection-anchor anchor-right",
                onMousedown: _withModifiers(($event: any) => (handleAnchorMouseDown($event, node.id)), ["stop"]),
                title: "Drag to connect nodes"
              }, [...(_cache[21] || (_cache[21] = [
                _createElementVNode("div", { class: "anchor-dot" }, null, -1)
              ]))], 40, _hoisted_19),
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("i", {
                  class: "fa fa-grip-vertical drag-handle",
                  style: _normalizeStyle({ color: node.forecolor || '#212529' })
                }, null, 4),
                _createElementVNode("span", {
                  class: "node-title",
                  style: _normalizeStyle({ color: node.forecolor || '#212529' })
                }, _toDisplayString(node.title), 5),
                _createElementVNode("div", _hoisted_21, [
                  (
                    isCategory(node) &&
                    node.children &&
                    node.children.length > 0
                  )
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "btn btn-sm btn-link text-primary node-collapse",
                        onClick: _withModifiers(($event: any) => (handleToggleCollapse(node.id)), ["stop"]),
                        title: 
                    node.collapsed ? 'Expand children' : 'Collapse children'
                  
                      }, [
                        _createElementVNode("i", {
                          class: _normalizeClass(
                      node.collapsed
                        ? 'fa fa-chevron-right'
                        : 'fa fa-chevron-down'
                    )
                        }, null, 2)
                      ], 8, _hoisted_22))
                    : _createCommentVNode("", true),
                  (isCategory(node))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        class: "btn btn-sm btn-link text-success node-add-child",
                        onClick: _withModifiers(($event: any) => (showAddChildModal(node.id)), ["stop"]),
                        title: "Add child node"
                      }, [...(_cache[22] || (_cache[22] = [
                        _createElementVNode("i", { class: "fa fa-plus" }, null, -1)
                      ]))], 8, _hoisted_23))
                    : _createCommentVNode("", true),
                  (node.isDeletable !== false)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 2,
                        class: "btn btn-sm btn-link text-danger node-delete",
                        onClick: _withModifiers(($event: any) => (handleDeleteNode(node.id)), ["stop"]),
                        title: "Delete node"
                      }, [...(_cache[23] || (_cache[23] = [
                        _createElementVNode("i", { class: "fa fa-times" }, null, -1)
                      ]))], 8, _hoisted_24))
                    : _createCommentVNode("", true)
                ])
              ]),
              (node.description)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "node-description",
                    style: _normalizeStyle({ color: node.forecolor || '#6c757d' })
                  }, _toDisplayString(node.description), 5))
                : _createCommentVNode("", true),
              (
                node.slug ||
                (node.collapsed && node.children && node.children.length > 0)
              )
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    (node.slug)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(node.slug), 1))
                      : _createCommentVNode("", true),
                    (
                  node.collapsed && node.children && node.children.length > 0
                )
                      ? (_openBlock(), _createElementBlock("span", _hoisted_27, [
                          _cache[24] || (_cache[24] = _createElementVNode("i", { class: "fa fa-layer-group me-1" }, null, -1)),
                          _createTextVNode(_toDisplayString(getCollapsedVariableCount(node)) + " variable" + _toDisplayString(getCollapsedVariableCount(node) !== 1 ? "s" : "") + " hidden ", 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ], 46, _hoisted_17))
          }), 128)),
          (_openBlock(), _createElementBlock("svg", _hoisted_28, [
            _cache[25] || (_cache[25] = _createElementVNode("defs", null, [
              _createElementVNode("marker", {
                id: "arrowhead",
                markerWidth: "10",
                markerHeight: "10",
                refX: "9",
                refY: "3",
                orient: "auto"
              }, [
                _createElementVNode("polygon", {
                  points: "0 0, 10 3, 0 6",
                  fill: "#6c757d"
                })
              ])
            ], -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allConnections.value, (connection) => {
              return (_openBlock(), _createElementBlock("g", {
                key: connection.id
              }, [
                _createElementVNode("path", {
                  d: getConnectionPath(connection),
                  stroke: "transparent",
                  "stroke-width": "12",
                  fill: "none",
                  class: "connection-hit-area",
                  onClick: _withModifiers(($event: any) => (handleConnectionClick(connection.id)), ["stop"])
                }, null, 8, _hoisted_29),
                _createElementVNode("path", {
                  d: getConnectionPath(connection),
                  stroke: 
                  selectedConnectionId.value === connection.id ? '#007bff' : '#6c757d'
                ,
                  "stroke-width": selectedConnectionId.value === connection.id ? 3 : 2,
                  fill: "none",
                  "marker-end": "url(#arrowhead)",
                  class: "connection-path",
                  onClick: _withModifiers(($event: any) => (handleConnectionClick(connection.id)), ["stop"])
                }, null, 8, _hoisted_30)
              ]))
            }), 128))
          ]))
        ], 36),
        _createElementVNode("div", _hoisted_31, [
          _createElementVNode("div", _hoisted_32, [
            _createElementVNode("button", {
              class: "ctrl-btn",
              onClick: handleZoomIn,
              title: "Zoom In"
            }, [...(_cache[26] || (_cache[26] = [
              _createElementVNode("i", { class: "fa fa-plus" }, null, -1)
            ]))]),
            _createElementVNode("div", {
              class: "zoom-level-display",
              onClick: handleResetZoom,
              title: "Click to reset zoom"
            }, _toDisplayString(Math.round(zoom.value * 100)) + "% ", 1),
            _createElementVNode("button", {
              class: "ctrl-btn",
              onClick: handleZoomOut,
              title: "Zoom Out"
            }, [...(_cache[27] || (_cache[27] = [
              _createElementVNode("i", { class: "fa fa-minus" }, null, -1)
            ]))])
          ]),
          _cache[29] || (_cache[29] = _createElementVNode("div", { class: "controls-separator" }, null, -1)),
          _createElementVNode("div", _hoisted_33, [
            _createElementVNode("button", {
              class: "ctrl-btn",
              onClick: handleResetView,
              title: "Reset View"
            }, [...(_cache[28] || (_cache[28] = [
              _createElementVNode("i", { class: "fa fa-house" }, null, -1)
            ]))]),
            _createElementVNode("button", {
              class: _normalizeClass(["ctrl-btn", { 'ctrl-btn--active': isFullScreen.value }]),
              onClick: handleFitToScreen,
              title: isFullScreen.value ? 'Exit Fullscreen' : 'Fit to Screen'
            }, [
              _createElementVNode("i", {
                class: _normalizeClass(isFullScreen.value ? 'fa fa-compress' : 'fa fa-expand')
              }, null, 2)
            ], 10, _hoisted_34)
          ]),
          _cache[30] || (_cache[30] = _createStaticVNode("<div class=\"controls-separator\" data-v-04168770></div><div class=\"gesture-hint\" data-v-04168770><div class=\"hint-row\" data-v-04168770><i class=\"fa fa-hand-pointer\" data-v-04168770></i> Drag to pan</div><div class=\"hint-row\" data-v-04168770><i class=\"fa fa-search-plus\" data-v-04168770></i> Ctrl+Scroll zoom</div></div>", 2))
        ])
      ], 512),
      _createVNode(MindmapSidebar, {
        ref_key: "sidebarRef",
        ref: sidebarRef,
        "selected-node": selectedNode.value,
        "ai-response": aiResponse.value,
        "is-running-prompt": isRunningPrompt.value,
        "response-token-count": responseTokenCount.value,
        "selected-model": selectedModel.value,
        onCloseProperties: handleDeselectNode,
        onAddChildNode: handleAddChildNodeFromPanel,
        onRunPrompt: runFinalPrompt,
        onStopPrompt: stopPromptRun,
        onClearAiResponse: clearAiResponse,
        onCopyAiResponse: copyAiResponse
      }, null, 8, ["selected-node", "ai-response", "is-running-prompt", "response-token-count", "selected-model"])
    ], 2),
    _createElementVNode("div", _hoisted_35, [
      _createElementVNode("div", _hoisted_36, [
        _cache[32] || (_cache[32] = _createStaticVNode("<div class=\"prompt-header-left\" data-v-04168770><div class=\"prompt-icon\" data-v-04168770><i class=\"fa fa-wand-magic-sparkles\" data-v-04168770></i></div><div data-v-04168770><h6 class=\"mb-0\" data-v-04168770>Final Prompt</h6><small class=\"text-muted\" data-v-04168770>Auto-generated from canvas nodes</small></div></div>", 1)),
        _createElementVNode("div", _hoisted_37, [
          (finalPrompt.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_38, _toDisplayString(finalPrompt.value.length) + " chars", 1))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: "toolbar-btn toolbar-btn--light",
            onClick: copyFinalPrompt,
            title: "Copy prompt to clipboard"
          }, [...(_cache[31] || (_cache[31] = [
            _createElementVNode("i", { class: "fa fa-copy" }, null, -1),
            _createElementVNode("span", null, "Copy", -1)
          ]))])
        ])
      ]),
      _createElementVNode("div", _hoisted_39, [
        (finalPrompt.value)
          ? (_openBlock(), _createElementBlock("pre", _hoisted_40, _toDisplayString(finalPrompt.value), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_41, [...(_cache[33] || (_cache[33] = [
              _createElementVNode("i", { class: "fa fa-diagram-project fa-2x mb-2" }, null, -1),
              _createElementVNode("p", null, "Add nodes to the canvas to generate the final prompt", -1)
            ]))]))
      ])
    ]),
    (showAddNodeModal.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "modal fade show d-block",
          tabindex: "-1",
          style: {"background-color":"rgba(0, 0, 0, 0.5)"},
          onClick: _withModifiers(closeAddNodeModal, ["self"])
        }, [
          _createElementVNode("div", _hoisted_42, [
            _createElementVNode("div", _hoisted_43, [
              _createElementVNode("div", { class: "modal-header" }, [
                _cache[34] || (_cache[34] = _createElementVNode("h5", { class: "modal-title" }, "Add Child Node", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-close",
                  onClick: closeAddNodeModal
                })
              ]),
              _createElementVNode("div", _hoisted_44, [
                _createElementVNode("form", {
                  onSubmit: _withModifiers(submitNewNode, ["prevent"])
                }, [
                  _createElementVNode("div", _hoisted_45, [
                    _cache[35] || (_cache[35] = _createElementVNode("label", {
                      for: "nodeTitle",
                      class: "form-label"
                    }, "Title *", -1)),
                    _withDirectives(_createElementVNode("input", {
                      id: "nodeTitle",
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newNodeData.value.title) = $event)),
                      type: "text",
                      class: "form-control",
                      placeholder: "Enter node title",
                      required: ""
                    }, null, 512), [
                      [_vModelText, newNodeData.value.title]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_46, [
                    _cache[36] || (_cache[36] = _createElementVNode("label", {
                      for: "nodeSlug",
                      class: "form-label"
                    }, "Slug *", -1)),
                    _withDirectives(_createElementVNode("input", {
                      id: "nodeSlug",
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newNodeData.value.slug) = $event)),
                      type: "text",
                      class: "form-control",
                      placeholder: "Enter slug (e.g., node-title)",
                      required: ""
                    }, null, 512), [
                      [_vModelText, newNodeData.value.slug]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_47, [
                    _cache[37] || (_cache[37] = _createElementVNode("label", {
                      for: "nodeDescription",
                      class: "form-label"
                    }, "Description", -1)),
                    _withDirectives(_createElementVNode("textarea", {
                      id: "nodeDescription",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newNodeData.value.description) = $event)),
                      class: "form-control",
                      rows: "3",
                      placeholder: "Enter description (optional)"
                    }, null, 512), [
                      [_vModelText, newNodeData.value.description]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_48, [
                    _cache[39] || (_cache[39] = _createElementVNode("label", {
                      for: "nodeCategory",
                      class: "form-label"
                    }, "Category *", -1)),
                    _withDirectives(_createElementVNode("select", {
                      id: "nodeCategory",
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newNodeData.value.prompt_category_id) = $event)),
                      class: "form-select",
                      disabled: "",
                      required: ""
                    }, [
                      _cache[38] || (_cache[38] = _createElementVNode("option", { value: "" }, "Select a category", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: category._id,
                          value: category._id
                        }, _toDisplayString(category.title), 9, _hoisted_49))
                      }), 128))
                    ], 512), [
                      [_vModelSelect, newNodeData.value.prompt_category_id]
                    ]),
                    _cache[40] || (_cache[40] = _createElementVNode("small", { class: "text-muted" }, "Auto-selected from parent category node", -1))
                  ]),
                  _createElementVNode("div", _hoisted_50, [
                    _cache[42] || (_cache[42] = _createElementVNode("label", { class: "form-label" }, "Type", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newNodeData.value.type_id) = $event)),
                      class: "form-select"
                    }, [
                      _cache[41] || (_cache[41] = _createElementVNode("option", { value: "" }, "Select a type (optional)", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(variableTypes.value, (type) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: type._id,
                          value: type._id
                        }, _toDisplayString(type.title), 9, _hoisted_51))
                      }), 128))
                    ], 512), [
                      [_vModelSelect, newNodeData.value.type_id]
                    ])
                  ])
                ], 32)
              ]),
              _createElementVNode("div", _hoisted_52, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-secondary",
                  onClick: closeAddNodeModal
                }, " Cancel "),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-success",
                  onClick: submitNewNode,
                  disabled: isCreatingNode.value
                }, [
                  _cache[43] || (_cache[43] = _createElementVNode("i", { class: "fa fa-plus me-1" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(isCreatingNode.value ? "Creating..." : "Create Node"), 1)
                ], 8, _hoisted_53)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})